import { useCallback, useEffect, useMemo, useState } from "react";
import { IconComponent } from "../components/atoms/icons/types";

export type TableHeaderItem<DataType> = {
  id: number;
  title: string;
  dataKey: string;
  popupTitle?: string;
  dataMapFunction?: (data: DataType) => any;
  dataMapFunctionForPopup?: (data: DataType) => any;
  showInTable?: boolean;
  customComponent?: React.FC<any>;
  sortOrder?: "asc" | "desc";
  sortKey?: string;
  icon?: IconComponent;
  disabled?: boolean;
  onClick?: () => void;
  width?: string;
};

export type TableSortBy = {
  field: string;
  order: "asc" | "desc";
};

export type TablePagination = {
  page: number;
  size: number;
};

type Props<DataType> = {
  headers: TableHeaderItem<DataType>[];
  data: DataType[];
  fetchDataFunction: (sort: TableSortBy, pagination?: TablePagination) => void;
  defaultSort?: TableSortBy;
  perPage?: number;
  total: number;
  currentPage?: number;
};

export const useTable = <
  DataType extends { selected?: boolean; disabled?: boolean },
>({
  headers,
  data,
  fetchDataFunction,
  defaultSort,
  perPage,
  total,
  currentPage: controlledPage,
}: Props<DataType>) => {
  // * State
  const [sortBy, setSortBy] = useState<TableSortBy | null>(defaultSort ?? null);
  const [currentPage, setCurrentPage] = useState<number>(controlledPage ?? 1);

  // Sync controlled currentPage from props
  useEffect(() => {
    if (controlledPage !== undefined && controlledPage !== currentPage) {
      setCurrentPage(controlledPage);
    }
  }, [controlledPage]);

  // * Effects

  useEffect(() => {
    if (sortBy === null) {
      const hasSortKey = headers.find(
        (header) => header.sortKey === defaultSort?.field,
      )
        ? headers.find((header) => header.sortKey === defaultSort?.field)
        : headers.find((header) => header.sortKey);
      if (!hasSortKey) return;

      setSortBy({
        field: hasSortKey.sortKey ?? "",
        order: defaultSort?.order ?? "asc",
      });
      return;
    }

    // Only call fetchDataFunction if we're not using controlled pagination
    // or if this is the initial sort setup
    if (controlledPage === undefined) {
      fetchDataFunction(
        sortBy === null
          ? { field: headers[0].dataKey, order: "asc" }
          : { field: sortBy.field, order: sortBy.order },
        perPage ? { size: perPage, page: currentPage } : undefined,
      );
    }
  }, [sortBy, currentPage, controlledPage]);

  // * Memos
  const totalPage = useMemo(() => {
    if (!perPage) return 1;

    return Math.ceil(total / perPage);
  }, [total, perPage]);

  const disableBulkDeleteButton = useMemo(() => {
    return !data.some((item) => item.selected && !item.disabled);
  }, [data]);

  const headersToShow = useMemo(() => {
    return headers.map((header) => {
      return {
        ...header,
        sortOrder: sortBy?.field === header.sortKey ? sortBy?.order : undefined,
        onClick: () => {
          if (header.disabled) return;
          header.onClick?.();

          if (!header.sortKey) return;

          if (!sortBy) return;

          const currentOrder = defaultSort?.order ?? "asc";
          const nextOrder = currentOrder === "asc" ? "desc" : "asc";

          if (sortBy.field === header.sortKey && sortBy.order === nextOrder) {
            const hasSortKey = headers.find(
              (header) => header.sortKey === defaultSort?.field,
            )
              ? headers.find((header) => header.sortKey === defaultSort?.field)
              : headers.find((header) => header.sortKey);
            if (!hasSortKey) return;

            setSortBy({
              field: hasSortKey.sortKey ?? "",
              order: defaultSort?.order ?? "asc",
            });
          } else {
            setSortBy({
              field: header.sortKey,
              order:
                sortBy.field === header.sortKey && sortBy.order === currentOrder
                  ? nextOrder
                  : currentOrder,
            });
          }
        },
      };
    });
  }, [headers, sortBy]);

  const dataToShow = useMemo(() => {
    if (sortBy === null) return data;

    return data;
  }, [data, sortBy]);

  const onNextPage = useCallback(() => {
    if (totalPage === 0 || currentPage === totalPage) return;
    setCurrentPage((prev) => prev + 1);
  }, [currentPage, totalPage]);

  const onPreviousPage = useCallback(() => {
    if (totalPage === 0 || currentPage === 1) return;

    setCurrentPage((prev) => prev - 1);
  }, [currentPage, totalPage]);

  return {
    headersToShow,
    dataToShow,
    currentPage,
    totalPage,
    disableBulkDeleteButton,
    setCurrentPage,
    onNextPage,
    onPreviousPage,
  };
};
